<template>
  <v-row justify="center">
    <v-dialog :value="show" overlay-opacity="0.4" overlay-color="#ffffff" persistent>
      <v-card flat>
        <v-overlay :absolute="true" :value="loading">
          <v-row align="center" justify="center">
            <v-col align="center">
              <v-progress-circular indeterminate color="info" :size="350">
              </v-progress-circular>
            </v-col>
          </v-row>
        </v-overlay>
        <v-card-title class="text-h5 blue darken-4 white--text">
          <v-toolbar-title>{{
            `${(currentItem && currentItem.sourceId) || isView ? "View" : "New"} Alert`
            }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="onCancel()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-alert v-if="saveError" type="error" dense role="alert">{{ saveError }}</v-alert>
          <v-row>
            <v-col cols="6">
              <v-card flat>
                <v-card-text>
                  <p class="text-h5 text--primary">
                    <v-icon left color="grey">mdi-cog</v-icon>
                    {{ $t("Alert info") }}
                  </p>

                  <v-form ref="form" v-model="valid" lazy-validation>
                    
                    <v-switch
                      v-if="['ADMIN', 'SUPERADMIN'].includes(user.role)"
                      label="internal" v-model="internal"
                      :disabled="isView">
                    </v-switch>

                    <v-radio-group :disabled="!!currentItem.sourceId || isView" v-model="currentItem.objectType" row
                      label="Object Type" @change="changeObjectType">
                      <v-radio label="Structure" value="STRUCTURE"> </v-radio>
                      <v-radio label="Element" value="ELEMENT"> </v-radio>
                    </v-radio-group>

                    <v-select :disabled="!!currentItem.sourceId || isView" v-if="currentItem.objectType === 'STRUCTURE'"
                      v-model="object" :rules="[rules.required]" :items="structures" label="Object" item-text="label"
                      item-value="structureId" return-object @change="changeObject">
                    </v-select>
                    <v-select :disabled="!!currentItem.sourceId || isView" v-if="currentItem.objectType === 'ELEMENT'"
                      v-model="object" :rules="[rules.required]" :items="elements" label="Object" item-text="label"
                      item-value="elementId" return-object @change="changeObject">
                    </v-select>

                    <v-text-field :disabled="!!currentItem.sourceId || isView" label="Name" v-model="currentItem.name"
                      required :rules="[rules.required]"></v-text-field>

                    <v-textarea :disabled="!!currentItem.sourceId || isView" label="Description"
                      v-model="currentItem.description"></v-textarea>

                    <v-select :disabled="!!currentItem.sourceId || isView" v-model="selectedLevel" :items="levels"
                      label="Level" item-text="label" item-value="id" return-object :rules="[rules.required]">
                    </v-select>
                    <v-select :disabled="!!currentItem.sourceId || isView" v-model="selectedFamily" :items="families"
                      label="Family" item-text="algorithmFamily" item-value="algorithmFamilyId" @change="changeFamily"
                      return-object :rules="[rules.required]">
                    </v-select>
                    <v-select :disabled="!selectedFamily || !!currentItem.sourceId || isView"
                      v-model="selectedAlgorithm" :items="algorithms" label="Algorithm" item-text="algorithm"
                      item-value="algorithmId" return-object @change="changeAlgorithm" :rules="[rules.required]">
                    </v-select>
                    <v-select :disabled="!selectedAlgorithm || !!currentItem.sourceId || isView"
                      v-model="selectedProperty" :items="selectableProperties" label="Property" item-text="displayLabel" item-value="id"
                      return-object :rules="[rules.required]">
                    </v-select>
                    <v-select :disabled="!!currentItem.sourceId || isView" v-model="selectedOperator" :items="operators"
                      label="Operator" item-text="operatorLabel" item-value="operatorId" return-object
                      :rules="[rules.required]">
                    </v-select>
                    <v-text-field :disabled="!selectedOperator || isView" label="Value1" type="number"
                      v-model.number="currentItem.value1" required></v-text-field>
                    <v-text-field :disabled="!selectedOperator || isView" v-if="value2Visibility" label="Value2"
                      type="number" v-model.number="currentItem.value2" required></v-text-field>
                  </v-form>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col v-if="!currentItem.sourceId" cols="6">
              <v-card flat>
                <v-card-text>
                  <p class="text-h5 text--primary">
                    <v-icon left color="grey">mdi-folder-multiple-outline</v-icon>
                    {{ $t("Entities") }}
                  </p>
                  <v-data-table v-model="selectedEntities" :headers="[{ text: 'Entities', value: 'label' }]"
                    :items="entities" item-key="objectId" :show-select="!currentItem.id" :items-per-page="10">
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn v-if="!currentItem.sourceId && !currentItem.id && !isView" @click="saveAlert()" color="success">
            <v-icon left>mdi-floppy</v-icon>Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>


<script>
 import { mapState } from 'vuex';
 import synOaqMeasurements from 'measurement-catalog/syn-oaq.json';

 const synOaqMeasures = new Map(synOaqMeasurements.map(measure => [measure.code, measure]));
 const rangeOperators = ['BTW', 'NOTBTW', 'BTWE', 'NOTBTWE'];

export default {
  name: "AlertEdit",
  props: [
    "show",
    "onSave",
    "onCancel",
    "selectedItem",
    "preconfiguredObjType",
    "entityId",
    "isView"
  ],
  components: {},
  watch: {
    selectedItem: function (newVal) {
      this.object = null;
      this.selectedFamily = null;
      this.selectedAlgorithm = null;
      this.selectedProperty = null;
      this.selectedOperator = null;
      this.selectedLevel = null;
      this.entities = [];
      this.selectedEntities = [];
      this.internal = null;

      this.saveError = '';
      return this.loadData(newVal);
    },
    selectedEntities() {
      if (this.selectedProperty && !this.selectableProperties.some(property => property.id === this.selectedProperty.id)) {
        this.selectedProperty = null;
      }
    },
  },
  computed: {
    ...mapState( [
      'user',
    ]),
    
    rules() {
      return this.$script.formRules;
    },

    selectableProperties() {
      const selected = this.selectedEntities || [];
      // SYN-OAQ has the fixed catalog ID 4 required by this integration.
      const hasSynOaq = selected.some(entity => entity.sensorSeriesId === 4);
      const mixed = hasSynOaq && selected.some(entity => entity.sensorSeriesId !== 4);
      return this.properties.filter(property => {
        const measure = synOaqMeasures.get(property.name);
        if (!hasSynOaq) return !measure || ['temperature', 'voltage'].includes(measure.name);
        return this.selectedAlgorithm?.algorithm === 'ONE_POINT' && measure &&
          (!mixed || ['temperature', 'voltage'].includes(measure.name));
      }).map(property => {
        const measure = synOaqMeasures.get(property.name);
        return { ...property, displayLabel: measure ? `${measure.label} (${measure.acronym})` : property.label };
      });
    },

    value2Visibility() {
      if (!this.selectedOperator) {
        return false
      }
      return rangeOperators.includes(this.selectedOperator.operatorName)
    }
  },
  data() {
    return {
      loading: false,
      saveError: '',
      entityLoadVersion: 0,
      algorithmLoadVersion: 0,
      currentItem: {},
      families: [],
      familiesAll: [],
      familiesStructures: [],
      algorithms: [],
      entities: [],
      object: null,
      selectedFamily: null,
      selectedLevel: null,
      selectedAlgorithm: null,
      selectedProperty: null,
      selectedOperator: null,
      value1: null,
      value2: null,
      structures: [],
      elements: [],
      properties: [],
      operators: [],
      levels: [],
      valid: false,
      selectedEntities: [],
      internal: false,
      entityType: "ENSEMBLE"
    };
  },
  methods: {
    async loadData(alertFromList) {
      console.log("alert:", alertFromList);
      let alert = { ...alertFromList };
      this.loading = true;
      if (alertFromList.id) {
        alert = await this.$store.dispatch("_getAlertDefinitionDetail", { idDefinedAlert: alertFromList.id })
        this.currentItem = alert;
      } else {
        this.detail = {};
        this.currentItem = {
          objectType: "STRUCTURE",
          ...alert,
        };
      }

      this.internal = alert.internal ?? false;
      if (alert.structureId) {
        this.currentItem.objectType = "STRUCTURE";
      }

      if (alert.elementId) {
        this.currentItem.objectType = "ELEMENT";
      }

      if (this.currentItem.objectType === "STRUCTURE") {
        await this.loadStructure();
      } else {
        await this.loadElements();
      }

      await this.loadFamily();

      await this.loadOperators();

      await this.loadLevels();

      this.loading = false;
      this.$nextTick(() => this.$refs.form?.resetValidation());
    },

    async loadStructure() {
      this.structures = await this.$store.dispatch("_getStructures");
      if (this.currentItem.structureId) {
        this.object = this.structures.find(
          (x) => x.structureId === this.currentItem.structureId
        );
      }
    },

    async loadElements() {
      this.elements = await this.$store.dispatch("_getElements");
      if (this.currentItem.elementId) {
        this.object = this.elements.find(
          (x) => x.elementId === this.currentItem.elementId
        );
      }
    },

    async loadEntities(entityType, algorithmId, objectId) {
      const version = ++this.entityLoadVersion;
      this.entities = [];
      this.selectedEntities = [];
      this.loading = true;
      if (!this.currentItem.id && algorithmId && objectId) {
        const entities = await this.$store.dispatch("getEnsembleEntities", {
          params: {
            algorithmId,
            objectId,
            entityType,
          },
        });
        if (version !== this.entityLoadVersion) return;
        this.entities = entities || [];
      }

      if (this.currentItem.id) {
        this.entities = this.currentItem.entities || [];
      }

      this.loading = false;
    },

    async loadFamily() {
      this.loading = true;
      this.selectedFamily = null;
      this.familiesAll = await this.$store.dispatch("getEnsembleFamilies");
      this.familiesStructures = this.familiesAll.filter(
        (x) => ["STATISTIC", "RAW"].includes(x.algorithmFamily)
      );

      if (this.currentItem && this.currentItem.objectType === "STRUCTURE") {
        this.families = this.familiesStructures;
      } else {
        this.families = this.familiesAll;
      }

      if (this.currentItem.algorithmFamilyId) {
        this.selectedFamily = this.families.find(x => x.algorithmFamilyId === this.currentItem.algorithmFamilyId)
      }

      if (this.selectedFamily) {
        await this.changeFamily(this.selectedFamily);
      }
      this.loading = false;
    },

    async loadAlgorithm(algorithmFamilyId) {
      const version = ++this.algorithmLoadVersion;
      this.loading = true;
      const algorithms = await this.$store.dispatch("getEnsembleAlgorithms", {
        params: {
          algorithmFamilyId,
        },
      });

      if (version !== this.algorithmLoadVersion) return;
      this.algorithms = (algorithms || []).filter(algorithm => this.currentItem.objectType !== 'STRUCTURE' ||
        this.selectedFamily?.algorithmFamily !== 'RAW' || algorithm.algorithm === 'ONE_POINT');
      if (this.currentItem.algorithmId) {
        this.selectedAlgorithm = this.algorithms.find(x => x.algorithmId === this.currentItem.algorithmId)
      }

      this.loading = false;
      if (this.selectedAlgorithm) {
        await this.changeAlgorithm(this.selectedAlgorithm);
      }
    },

    async loadOperators() {
      this.loading = true;
      this.operators = await this.$store.dispatch("_getAlertsOperators");
      this.loading = false;

      if (this.currentItem.alertOperatorId) {
        this.selectedOperator = this.operators.find(x => x.operatorId === this.currentItem.alertOperatorId)
      }
    },

    async loadLevels() {
      this.loading = true;
      this.levels = await this.$store.dispatch("_getAlertsLevels");
      this.loading = false;

      if (this.currentItem.alertLevelId) {
        this.selectedLevel = this.levels.find(x => x.id === this.currentItem.alertLevelId)
      }
    },

    async changeFamily(family) {
      this.selectedFamily = family;
      this.selectedAlgorithm = null;
      this.selectedProperty = null;
      this.properties = [];
      this.entities = [];
      this.selectedEntities = [];
      this.entityLoadVersion++;
      await this.loadAlgorithm(family.algorithmFamilyId);
    },

    async changeAlgorithm(algorithm) {
      this.selectedAlgorithm = algorithm;
      this.selectedProperty = null;
      this.properties = algorithm?.outputParams || [];
      this.entities = [];
      this.selectedEntities = [];
      this.entityLoadVersion++;
      if (this.object && algorithm) {
                
        await this.loadEntities(
          this.entityType,
          algorithm.algorithmId,
          this.object.elementId || this.object.structureId
        );
      }
      if (this.currentItem.algorithmOutputParamId) {
        this.selectedProperty = this.properties.find(x => x.id === this.currentItem.algorithmOutputParamId)
      }
    },

    async changeObjectType(objectType) {
      this.selectedFamily = null;
      this.selectedAlgorithm = null;
      this.selectedProperty = null;
      this.properties = [];
      this.entities = [];
      this.selectedEntities = [];
      this.object = null;
      this.algorithmLoadVersion++;
      this.entityLoadVersion++;
      this.loading = true;
      if (objectType === "STRUCTURE") {
        await this.loadStructure();
        this.families = this.familiesStructures;
      }
      if (objectType === "ELEMENT") {
        await this.loadElements();
        this.families = this.familiesAll;
      }
      this.loading = false;
    },

    async changeObject(object) {
      this.object = object;
      this.selectedProperty = null;
      this.selectedEntities = [];
      this.entities = [];
      this.entityLoadVersion++;
      this.loading = true;

      if (this.selectedAlgorithm) {

        await this.loadEntities(
          this.entityType,
          this.selectedAlgorithm.algorithmId,
          object.elementId || object.structureId
        );
      }
      this.loading = false;
    },

    async saveAlert() {
      if (this.isView || this.currentItem.id || this.currentItem.sourceId || this.loading) return;
      this.saveError = '';
      if (!this.$refs.form.validate()) return;
      const selectionValid = this.selectedEntities.length > 0 && this.selectedEntities.every(entity =>
        this.entities.some(candidate => candidate.objectId === entity.objectId));
      const propertyValid = this.selectedProperty && this.selectableProperties.some(property => property.id === this.selectedProperty.id);
      if (!this.object || !this.selectedAlgorithm || !this.selectedOperator || !this.selectedLevel || !selectionValid || !propertyValid) {
        this.saveError = 'Select an object, measure, operator, level and at least one compatible ensemble.';
        return;
      }
      const needsSecondValue = rangeOperators.includes(this.selectedOperator.operatorName);
      if (!Number.isFinite(this.currentItem.value1) || (needsSecondValue &&
          (!Number.isFinite(this.currentItem.value2) || this.currentItem.value1 > this.currentItem.value2))) {
        this.saveError = 'Enter valid numeric limits; the lower limit must not exceed the upper limit.';
        return;
      }
      this.loading = true;
      try {
        const payload = {
          objectIds: [...new Set(this.selectedEntities.map(entity => entity.objectId))],
          algorithmId: this.selectedAlgorithm.algorithmId,
          objectId: this.object.structureId || this.object.elementId,
          name: this.currentItem.name,
          description: this.currentItem.description,
          algorithmOutputParamId: this.selectedProperty.id,
          alertOperatorId: this.selectedOperator.operatorId,
          alertLevelId: this.selectedLevel.id,
          value1: this.currentItem.value1,
          value2: needsSecondValue ? this.currentItem.value2 : null,
          internal: this.internal,
        };
        const saved = await this.$store.dispatch('_saveAlert', payload);
        if (!saved) throw new Error('Alert not saved');
        this.$emit('refresh');
        this.$props.onSave();
      } catch {
        this.saveError = 'Unable to save the alert. Check the selection and try again.';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
